import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Scales, ArrowsOutCardinal, Check, Info, Stack } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

export default function ScaleRecipe({ recipe, onApply }) {
  const currentPortions = Math.max(1, parseInt(recipe.num_portions || 1, 10));
  const currentPortionSize = parseFloat(recipe.portion_size_g || 0);
  const currentTotalG = currentPortions * currentPortionSize;
  const currentTotalKg = currentTotalG / 1000;

  const [mode, setMode] = useState("portions"); // 'portions' | 'weight' | 'factor'
  const [targetPortions, setTargetPortions] = useState(currentPortions);
  const [targetKg, setTargetKg] = useState(currentTotalKg || 1);
  const [factor, setFactor] = useState(1);
  const [scaleOverheads, setScaleOverheads] = useState(false);

  const computed = useMemo(() => {
    let f = 1;
    if (mode === "portions") {
      const tp = Math.max(1, parseInt(targetPortions || 1, 10));
      f = tp / currentPortions;
    } else if (mode === "weight") {
      const tkg = Math.max(0.01, parseFloat(targetKg || 0));
      f = (currentTotalKg > 0) ? tkg / currentTotalKg : 1;
    } else {
      f = Math.max(0.01, parseFloat(factor || 1));
    }
    const roundedFactor = Math.round(f * 1000) / 1000;
    const newPortions = Math.max(1, Math.round(currentPortions * roundedFactor));
    const newTotalKg = Math.round(currentTotalKg * roundedFactor * 100) / 100;
    return { factor: roundedFactor, newPortions, newTotalKg };
  }, [mode, targetPortions, targetKg, factor, currentPortions, currentTotalKg]);

  const apply = () => {
    if (!computed.factor || computed.factor <= 0) {
      toast.error("Invalid scale factor");
      return;
    }
    const f = computed.factor;
    const scaleLine = (arr) =>
      (arr || []).map((ing) => ({
        ...ing,
        quantity: Math.round((parseFloat(ing.quantity || 0) * f) * 1000) / 1000,
      }));

    const next = {
      ...recipe,
      num_portions: computed.newPortions,
      primary_ingredients: scaleLine(recipe.primary_ingredients),
      secondary_ingredients: scaleLine(recipe.secondary_ingredients),
    };

    if (scaleOverheads) {
      const scaleObj = (obj) =>
        Object.fromEntries(
          Object.entries(obj || {}).map(([k, v]) => [k, Math.round((parseFloat(v || 0) * f) * 100) / 100])
        );
      next.utilities = scaleObj(recipe.utilities);
      next.overheads = scaleObj(recipe.overheads);
    }

    onApply(next);
    toast.success(`Scaled to ${computed.newPortions} portion${computed.newPortions > 1 ? "s" : ""} (×${f})`);
  };

  const presetFactors = [0.5, 2, 3, 5, 10];

  return (
    <div className="kostra-card p-5 md:p-6" data-testid="scale-recipe-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[#0A3B24]/10 text-[#0A3B24]">
            <ArrowsOutCardinal size={20} weight="duotone" />
          </div>
          <div>
            <h3 className="kostra-section-title text-xl">Yield Calculator</h3>
            <p className="text-xs text-[#4B5563] mt-0.5">
              Scale every ingredient quantity proportionally for a larger or smaller batch.
            </p>
          </div>
        </div>
        <div className="gold-pill text-[11px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
          <Stack size={12} weight="fill" />
          Base: {currentPortions} × {currentPortionSize}g = {currentTotalKg.toFixed(2)} kg
        </div>
      </div>

      {/* Mode tabs */}
      <div className="inline-flex rounded-md border-2 border-[#E5E7EB] bg-white p-0.5 w-full md:w-auto mb-4" role="group">
        {[
          { id: "portions", label: "By Portions" },
          { id: "weight", label: "By Total Weight" },
          { id: "factor", label: "By Factor" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMode(t.id)}
            className={`flex-1 md:flex-none px-4 py-2 text-xs font-bold rounded-[5px] transition ${
              mode === t.id ? "bg-[#0A3B24] text-white" : "text-[#4B5563] hover:bg-[#0A3B24]/5"
            }`}
            data-testid={`scale-mode-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        {mode === "portions" && (
          <div className="md:col-span-1">
            <Label className="kostra-data-label mb-1.5 block">Target number of portions</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={targetPortions}
              onChange={(e) => setTargetPortions(parseInt(e.target.value, 10) || 1)}
              className="h-11 font-mono-num border-2"
              data-testid="scale-target-portions"
            />
          </div>
        )}
        {mode === "weight" && (
          <div className="md:col-span-1">
            <Label className="kostra-data-label mb-1.5 block">Target total weight</Label>
            <div className="relative">
              <Input
                type="number"
                min="0.01"
                step="0.1"
                value={targetKg}
                onChange={(e) => setTargetKg(parseFloat(e.target.value) || 0)}
                className="h-11 font-mono-num pr-12 border-2"
                data-testid="scale-target-kg"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0A3B24] font-bold text-sm">kg</span>
            </div>
          </div>
        )}
        {mode === "factor" && (
          <div className="md:col-span-1">
            <Label className="kostra-data-label mb-1.5 block">Scale factor</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A3B24] font-bold text-sm">×</span>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                value={factor}
                onChange={(e) => setFactor(parseFloat(e.target.value) || 0)}
                className="h-11 font-mono-num pl-8 border-2"
                data-testid="scale-target-factor"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {presetFactors.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setFactor(p)}
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] text-[#0A3B24] hover:bg-[#FDF9EE] hover:border-[#D4AF37]/50 transition"
                  data-testid={`scale-preset-${p}x`}
                >
                  ×{p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        <div className="md:col-span-2">
          <Label className="kostra-data-label mb-1.5 block">Preview</Label>
          <motion.div
            key={`${computed.factor}-${computed.newPortions}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-3 gap-2"
            data-testid="scale-preview"
          >
            <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Factor</div>
              <div className="font-mono-num text-xl font-semibold text-[#0A3B24] mt-0.5">×{computed.factor}</div>
            </div>
            <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Portions</div>
              <div className="font-mono-num text-xl font-semibold text-[#111827] mt-0.5">
                {currentPortions} → <span className="text-[#0A3B24]">{computed.newPortions}</span>
              </div>
            </div>
            <div className="rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Weight</div>
              <div className="font-mono-num text-xl font-semibold text-[#111827] mt-0.5">
                {currentTotalKg.toFixed(2)} → <span className="text-[#0A3B24]">{computed.newTotalKg.toFixed(2)}</span> <span className="text-sm">kg</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 pt-4 border-t border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-[#111827] cursor-pointer">
          <Checkbox
            checked={scaleOverheads}
            onCheckedChange={(v) => setScaleOverheads(!!v)}
            data-testid="scale-overheads-toggle"
          />
          <span>Also scale utilities &amp; overheads <span className="text-[#4B5563]">(recommended for batch kitchens)</span></span>
        </label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#4B5563] inline-flex items-center gap-1">
            <Info size={12} weight="duotone" /> All ingredient quantities will multiply by the factor
          </span>
          <Button
            type="button"
            onClick={apply}
            className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-11 px-5"
            data-testid="scale-apply-btn"
          >
            <Check size={16} weight="bold" className="mr-2" /> Apply Scaling
          </Button>
        </div>
      </div>
    </div>
  );
}
